/**
 * EL DIARIO POR SECCIONES.
 *
 * El cuerpo de una entrada sigue siendo UN texto —el que la API cifra en reposo o el
 * dispositivo cifra en modo privado—; dentro de él viajan las tres secciones como
 * JSON con versión. Así no cambia el esquema, no cambia el cifrado y no cambia la
 * sincronización.
 *
 * Una entrada que no se lee como v2 es una entrada de antes y se enseña entera en
 * «Hoy». Nunca se migra a la fuerza: se reescribe en v2 cuando el usuario la edita.
 */
export type SeccionesDiario = {
  readonly gracias: readonly [string, string, string];
  readonly hoy: string;
  readonly manana: string;
};

export const DIARIO_VACIO: SeccionesDiario = { gracias: ['', '', ''], hoy: '', manana: '' };

const texto = (v: unknown): string => (typeof v === 'string' ? v : '');

export function leerDiario(body: string): SeccionesDiario {
  if (body.startsWith('{')) {
    try {
      const d = JSON.parse(body) as Record<string, unknown>;
      if (d['v'] === 2 && Array.isArray(d['gracias'])) {
        const g = d['gracias'] as unknown[];
        return {
          gracias: [texto(g[0]), texto(g[1]), texto(g[2])],
          hoy: texto(d['hoy']),
          manana: texto(d['manana']),
        };
      }
    } catch {
      // Texto antiguo que empieza por llave: se queda como estaba.
    }
  }
  return { ...DIARIO_VACIO, hoy: body };
}

export function diarioVacio(s: SeccionesDiario): boolean {
  return s.gracias.every((g) => g.trim() === '') && s.hoy.trim() === '' && s.manana.trim() === '';
}

export function escribirDiario(s: SeccionesDiario): string {
  if (diarioVacio(s)) return '';
  return JSON.stringify({ v: 2, gracias: [...s.gracias], hoy: s.hoy, manana: s.manana });
}

/** Legible en cualquier editor: lo usan la exportación en Markdown y el copiloto. */
export function diarioEnTexto(s: SeccionesDiario): string {
  const gracias = s.gracias.map((g) => g.trim()).filter((g) => g !== '');
  const hoy = s.hoy.trim();
  const manana = s.manana.trim();
  if (gracias.length === 0 && manana === '') return hoy;

  const partes: string[] = [];
  if (gracias.length > 0) partes.push(`Gracias por:\n${gracias.map((g) => `- ${g}`).join('\n')}`);
  if (hoy !== '') partes.push(`Hoy:\n${hoy}`);
  if (manana !== '') partes.push(`Mañana:\n${manana}`);
  return partes.join('\n\n');
}

export function resumenDiario(s: SeccionesDiario): string {
  const gracia = s.gracias.find((g) => g.trim() !== '')?.trim();
  // Sin pasar a minúscula: lo más común aquí es un nombre («Gracias por Ana»).
  if (gracia) return `Gracias por ${gracia}`;
  const hoy = s.hoy.trim().split('\n')[0]?.trim();
  if (hoy) return hoy;
  const manana = s.manana.trim().split('\n')[0]?.trim();
  return manana ? `Mañana: ${manana}` : '';
}

/** Lo más largo que puede ser una tarea (como `task.title`). */
const MAX_TAREA = 140;

/**
 * LO DE «MAÑANA», EN TAREAS. El ritual ofrece pasar cada línea a una tarea de hoy:
 * se parte por renglones y por punto y coma, se quitan viñetas y números («-», «•»,
 * «1.», «2)»), y se descartan los vacíos y los repetidos. Así «Llamar a mamá; pagar
 * la luz» son dos tareas y no una.
 */
export function lineasDeManana(texto: string): string[] {
  const vistas = new Set<string>();
  const lineas: string[] = [];
  for (const cruda of texto.split(/\r?\n|;/)) {
    const limpia = cruda
      .replace(/^\s*(?:[-*•·–—]|\d+[.)])\s*/, '')
      .trim()
      .slice(0, MAX_TAREA);
    const clave = limpia.toLowerCase();
    if (limpia === '' || vistas.has(clave)) continue;
    vistas.add(clave);
    lineas.push(limpia);
  }
  return lineas;
}
